import { Routes } from '@angular/router';
import { MsalGuard, MsalRedirectComponent } from '@azure/msal-angular';
import { roleGuard } from '@app/core/guards/role.guard';
import { UserRole } from '@app/core/models/user-role';
import { roleCheckGuard } from '@app/core/guards/role-check.guard';
import { unsavedChangesGuard } from '@app/core/guards/unsaved-changes-guard';

export const routes: Routes = [
  {
    path: 'auth',
    component: MsalRedirectComponent,
  },

  /**
   * PROFILE
   * Minimum Role: User
   * */
  {
    path: '',
    title: 'Profiel | Grow@ABC',
    canActivate: [MsalGuard],
    loadComponent: () => import('./features/profile/profile').then((m) => m.Profile),
  },
  {
    path: 'profile/:id',
    title: 'Profiel | Grow@ABC',
    canActivate: [MsalGuard],
    loadComponent: () => import('./features/profile/profile').then((m) => m.Profile),
  },

  /**
   * COACHING: COACH VIEW
   * Minimum Role: Coach
   * */
  {
    path: 'coaching',
    canMatch: [roleCheckGuard(UserRole.COACH)],
    canActivate: [MsalGuard],
    loadComponent: () => import('./features/coaching/coaching').then((m) => m.Coaching),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'coachees',
      },
      {
        path: 'coachees',
        title: 'Coachees | Grow@ABC',
        loadComponent: () =>
          import('./features/coaching/components/coachees-overview/coachees-overview').then(
            (m) => m.CoacheesOverview,
          ),
      },
      {
        path: 'sessions-as-coach',
        title: 'Gesprekken als coach | Grow@ABC',
        loadComponent: () =>
          import('./features/coaching/components/sessions/sessions-as-coach/sessions-as-coach').then(
            (m) => m.SessionsAsCoach,
          ),
      },
      {
        path: 'sessions-as-coachee',
        title: 'Gesprekken als coachee | Grow@ABC',
        loadComponent: () =>
          import('./features/coaching/components/sessions/sessions-as-coachee/sessions-as-coachee').then(
            (m) => m.SessionsAsCoachee,
          ),
      },
    ],
  },
  {
    path: 'coaching/coachees/:id',
    title: 'Coachee Details | Grow@ABC',
    canActivate: [MsalGuard, roleGuard(UserRole.COACH)],
    loadComponent: () =>
      import('./features/coaching/components/coachees-overview/coachee-detail/coachee-detail').then(
        (m) => m.CoacheeDetail,
      ),
  },
  {
    path: 'coaching/session/:id',
    title: 'Coaching Gesprek | Grow@ABC',
    canActivate: [MsalGuard],
    canDeactivate: [unsavedChangesGuard],
    loadComponent: () =>
      import('./features/coaching/components/sessions/session-detail/session-detail').then(
        (m) => m.SessionDetail,
      ),
  },

  /**
   * COACHING: USER VIEW
   * Minimum Role: User
   */
  {
    path: 'coaching',
    title: 'Coaching | Grow@ABC',
    canActivate: [MsalGuard],
    loadComponent: () =>
      import('./features/coaching/components/coachee-view/coachee-view').then((m) => m.CoacheeView),
  },

  /**
   * GOALS
   * Minimum Role: User
   * */
  {
    path: 'goals',
    title: 'Doelen | Grow@ABC',
    canActivate: [MsalGuard],
    loadComponent: () => import('./features/goals/goals').then((m) => m.Goals),
  },

  /**
   * LEARNING HUB
   * Minimum Role: User
   * */
  {
    path: 'learning-hub',
    title: 'Leerbibliotheek | Grow@ABC',
    canActivate: [MsalGuard],
    loadComponent: () => import('./features/learning/learning').then((m) => m.Learning),
  },
  {
    path: 'learning-hub/:id',
    title: 'Cursus | Grow@ABC',
    canActivate: [MsalGuard],
    loadComponent: () =>
      import('./features/learning/components/course-detail/course-detail').then(
        (m) => m.CourseDetail,
      ),
  },

  /**
   * DASHBOARD
   * Minimum Role: Management
   * */
  {
    path: 'dashboard',
    title: 'Statistieken | Grow@ABC',
    canActivate: [MsalGuard, roleGuard(UserRole.MANAGEMENT)],
    loadComponent: () => import('./features/dashboard/dashboard').then((m) => m.Dashboard),
    children: [
      {
        path: '',
        title: 'Statistieken | Grow@ABC',
        loadComponent: () =>
          import('./features/dashboard/components/stats/stats').then((m) => m.Stats),
      },
      {
        path: 'employees',
        title: 'Werknemers | Grow@ABC',
        loadComponent: () =>
          import('./features/dashboard/components/employee-overview/employee-overview').then(
            (m) => m.EmployeeOverview,
          ),
      },
    ],
  },
  {
    title: 'Unauthorized | Grow@ABC',
    path: 'unauthorized',
    loadComponent: () =>
      import('./status-pages/unauthorized/unauthorized').then((m) => m.Unauthorized),
  },
  {
    title: 'Not Found | Grow@ABC',
    path: '**',
    loadComponent: () => import('./status-pages/not-found/not-found').then((m) => m.NotFound),
  },
];
